// ФУТЕР-НАВИГАЦИЯ — какая пара стоит внизу на каждом шаге.
//
// ИСТОЧНИК. Вариант A из ОВ-CC.1 (закрыт `s889`): `← back` уходит из верха экрана вниз, к CTA,
// и становится его парой. Хром-контракт `docs/design/wizard-browser-rules.md:32–36` (сфера
// `s785.d2`) прибивает эту пару к низу вьюпорта. Приёмка 6 тикета «Каркас»: на s1 и на шаге 7
// пары нет. С B8-5 то же на шаге 6: борд `Overlay v10` ряда не рисует, конец экрана — close-блок.
//
// ПОЧЕМУ ТАБЛИЦА, А НЕ УСЛОВИЕ В РАЗМЕТКЕ. Отсутствие контрола на шаге — предмет решения, а не
// вёрстки: `s785.d2` прямо выводит шаг 7 из-под липкого футера, потому что на нём объекта
// «ряд CTA + back» не заводится вовсе. В таблице это видно, в тернарнике внутри JSX — нет.
//
// ⚠️ ЧЕГО ЗДЕСЬ НЕТ. Блокировка CTA при незаполненном обязательном поле — по спеке шага, не здесь.
// ⭐ И С B4-4 У ЭТОГО ЕСТЬ ИСПОЛНЕНИЕ, А НЕ ТОЛЬКО ОГОВОРКА. Гейт шага 2 (`s461`) приходит
// КАНАЛОМ: экран объявляет состояние кнопки (`publishGate` в `WizardProvider.jsx`, арифметика —
// `steps/stageGate.js`), оболочка его печатает. Таблица при этом не трогается вовсе: она
// отвечает на вопрос «какая пара стоит внизу», а не «можно ли сейчас идти» — и надпись
// ОТКРЫТОГО состояния по-прежнему её, а не шага.
//
// ⭐ ПАРА `continue` + `start over` ТЕПЕРЬ ЖИВЁТ ЗДЕСЬ (B3-4, слово web-Claude по развилке).
// До cc-130 в этой шапке стояло обратное: «это состояние лендинга, а не ряд навигации», то есть
// пару рисует экран. Развилка была поднята тикетом (расширить таблицу против второго пропа
// `renderFooter` у оболочки) и закрыта в пользу таблицы. Довод: борд v2 называет состояние 2
// «меняются ДВА ОБЪЕКТА ФУТЕРА и ничего больше», и если эти два объекта заводит экран, то
// источников «что стоит в ряду» становится два — ровно то, против чего эта таблица и написана.
// Экран s1 от состояния входа не меняется ни строкой (тело кадров борда совпадает байт в байт).
//
// ⚠️ СЛОВАРЬ. Только слово `back` — унификация ОВ-CC.1; `prev` / `previous` / `move` не
// использовать. Надпись CTA: на шаге 1 — `begin` (борд Landing, `wizard-browser-rules.md:112`,
// где `begin reading` сокращён до `begin`), на шагах 2–5 — `continue` (ОВ-CC.1).
//
// ⭐ СТРЕЛКИ В НАДПИСИ БОЛЬШЕ НЕТ (правка cc-129, решение web-Claude как копи-владельца).
// Здесь стояло `'continue →'` — стрелка внутри строки, — а на шаге 1 её не было вовсе. Борд
// Landing v2 рисует стрелку ПСЕВДОЭЛЕМЕНТОМ `.cta::after` для всех состояний: его `begin`
// читается как `begin →`, его `continue` — как `continue →`. То есть код расходился с бордом
// в обе стороны сразу: лишняя стрелка в данных и недостающая на экране.
//
// ⚠️ ОВ-CC.1 ЭТИМ НЕ НАРУШЕН, хотя и пишет пару как `← back   continue →`. Он описывает то,
// что человек ВИДИТ, а видит он ровно это: слово из таблицы плюс стрелка из css. Разошлись не
// решения, а слои — надпись и её оформление. Теперь `ctaLabel` — чистая копи, и парити
// сверяет её с байтом борда напрямую (`__parity__/wizard-s1-copy.test.jsx`).

import { STEP_COUNT, stepNumber } from './address.js';

/** Надпись возврата. Одна на весь визард. */
export const BACK_LABEL = '← back';

/** Надпись тихого выхода — ghost-ссылка под CTA в состоянии 2 (слот `s1.resume.restart`). */
export const RESTART_LABEL = 'start over';

/** Надпись CTA по шагам: вход называется `begin`, дальше — `continue`. Стрелку даёт css. */
const CTA_LABEL = { 1: 'begin', default: 'continue' };

/** Ряда нет вовсе — шаги 6 и 7. Отдельной константой, чтобы «пусто» читалось как решение. */
const NO_ROW = { back: false, cta: false, ctaLabel: null, backLabel: null, restart: false, restartLabel: null };

/**
 * Что стоит в футере на этом адресе.
 *
 * ⭐ ВТОРОЙ АРГУМЕНТ — СОСТОЯНИЕ ВХОДА (`s863.d1`, исполнение `s895.d1`). `hasWork` — предикат
 * конверта (`session.js`), и на шаге 1 он выбирает между двумя кадрами борда: пустой ключ даёт
 * `begin` (состояние 1), непустой — `continue` плюс ghost-ссылку `start over` (состояние 2).
 * Дальше первого шага он ничего не меняет: состояние входа это свойство ВХОДА, а не всех шагов.
 *
 * @param {string} address адрес шага (`s1`, `s3/2/b`)
 * @param {{hasWork?: boolean}} [state] состояние конверта
 * @returns {{back: boolean, cta: boolean, ctaLabel: string|null, backLabel: string|null,
 *            restart: boolean, restartLabel: string|null}}
 */
export function footerFor(address, { hasWork = false } = {}) {
  const step = stepNumber(address);
  if (step < 1 || step > STEP_COUNT) {
    throw new Error(`футер требует шаг 1..${STEP_COUNT}, получено: ${address}`);
  }

  // Шаг 7 — терминальный: ряда CTA + back там нет, контрола `back` нет (`s785.d2`).
  // Шаг 6 — тоже без ряда (B8-5): борд `Overlay v10` `:67–71` кончает экран блоком `.close` с одной
  // `.cta` `Get the full reading →` в гейт, ни `continue`, ни `← back` у него нет. CTA рисует экран, а
  // не оболочка — значит и липкости ряда (`s785.d2`) на шаге 6 не к чему применяться.
  if (step === STEP_COUNT || step === STEP_COUNT - 1) return { ...NO_ROW };

  // Шаг 1 — начало: возвращаться внутри визарда некуда, `back` нет ни в одном из состояний.
  if (step === 1) {
    if (!hasWork) {
      return { back: false, cta: true, ctaLabel: CTA_LABEL[1], backLabel: null, restart: false, restartLabel: null };
    }
    // Состояние 2. Надпись CTA — `continue` (борд Landing v2, кадр 2); номера шага в кнопке нет
    // (дубль счётчика снят, пункт 10 борда). ⚠️ Замер B8-5: как ПОДПИСЬ CTA слово `continue` печатает
    // только этот борд. Борды 2–5 несут свои экранные подписи (`add …`, `open card I · …`, `find the
    // pattern`, `see it over your memories`), борд 6 — `Get the full reading →`. `continue` на шагах
    // 2–5 — из ОВ-CC.1 (надпись открытого состояния по умолчанию), а не байт их бордов.
    return {
      back: false,
      cta: true,
      ctaLabel: CTA_LABEL.default,
      backLabel: null,
      restart: true,
      restartLabel: RESTART_LABEL,
    };
  }

  return { back: true, cta: true, ctaLabel: CTA_LABEL.default, backLabel: BACK_LABEL, restart: false, restartLabel: null };
}

/** Есть ли на этом шаге ряд футер-навигации вообще (к нему относится липкость, `s785.d2`). */
export function hasFooterRow(address, state) {
  const { back, cta, restart } = footerFor(address, state);
  return back || cta || restart;
}
